import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getFileUrl, getPublicAuthorPhotoUrl, libraryAPI, publicAPI } from "@/services/api";
import { wakeBackend } from "@/services/backendWake";
import MobileBottomNav from "@/components/app/MobileBottomNav";
import articoLogo from "@/assets/artico-logo.png";
import { formatCurrency, PRODUCT_TYPES } from "@/utils/constants";
import { getProductCategoryLabel } from "@/utils/productCategories";
import {
  ArrowRight,
  BookOpen,
  FileText,
  Headphones,
  LayoutGrid,
  Library,
  LogIn,
  MessageCircle,
  Shield,
  ShoppingBag,
  Sparkles,
  UserCircle2,
} from "lucide-react";

const MOBILE_MMJ_INTRO = "MMJ est une communauté numérique où les créateurs, lecteurs et partenaires découvrent, partagent, valorisent des œuvres transformée en opportunité de croissance. Rejoignez notre communauté de partenaires et bénéficiez d'un système de rémunération équitable.";

function getItemKindLabel(product) {
  if (!product) return "Oeuvre";
  return PRODUCT_TYPES[product.product_type]?.label || "Oeuvre";
}

function MobileCoverCard({ item, onOpen, dense = false }) {
  const coverPath = item.thumbnail_path || item.cover_image_path;
  const coverUrl = coverPath ? getFileUrl(coverPath) : null;
  const authorPhotoUrl = item.author_profile_photo_path ? getPublicAuthorPhotoUrl(item.author_profile_photo_path) : null;
  const isFree = item.is_free_for_active_par || Number(item.price_mc || 0) <= 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group overflow-hidden rounded-3xl border border-[#E6E4E0] bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        dense ? "min-w-[180px]" : "min-w-[210px]"
      }`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#263A36]">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={item.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#2A4B46] to-[#D4A373]">
            {item.product_type === "texte_sonorise" ? (
              <Headphones className="text-white" size={34} />
            ) : item.product_type === "texte_chante" ? (
              <Sparkles className="text-white" size={34} />
            ) : (
              <BookOpen className="text-white" size={34} />
            )}
          </div>
        )}
        {authorPhotoUrl && (
          <div className="absolute bottom-3 left-3 h-12 w-12 overflow-hidden rounded-full border-2 border-white bg-white shadow-md">
            <img src={authorPhotoUrl} alt={item.author_name || "Auteur"} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
        )}
        <div className="absolute right-3 top-3 rounded-full bg-black/65 px-2.5 py-1 text-[11px] font-semibold text-white">
          {isFree ? "Gratuit" : formatCurrency(item.price_mc)}
        </div>
      </div>
      <div className="space-y-2 p-3">
        <div className="space-y-1">
          <p className="line-clamp-2 text-sm font-semibold leading-5 text-[#2A4B46] font-['Outfit']">{item.title}</p>
          <p className="text-[11px] text-[#6E746F]">{item.author_name || "ARTICO"}</p>
        </div>
        {!dense && (
          <p className="line-clamp-2 text-xs leading-5 text-[#5C625F]">{item.description}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <Badge className="rounded-full bg-[#F3EFE9] text-[11px] text-[#2A4B46]">{getItemKindLabel(item)}</Badge>
          {item.subcategory ? (
            <Badge className="rounded-full bg-[#E7EFEC] text-[11px] text-[#2A4B46]">
              {getProductCategoryLabel(item.category)} / {item.subcategory}
            </Badge>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function RoleShortcut({ href, icon: Icon, title, description }) {
  return (
    <Link to={href} className="rounded-3xl border border-[#E6E4E0] bg-white p-4 shadow-sm transition hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#2A4B46] text-white">
          <Icon size={20} />
        </div>
        <span className="min-w-0">
          <p className="font-semibold text-[#2A4B46]">{title}</p>
          <p className="mt-1 text-xs leading-5 text-[#5C625F]">{description}</p>
        </span>
      </div>
    </Link>
  );
}

export default function MobileHomePage({ user }) {
  const navigate = useNavigate();
  const [libraryItems, setLibraryItems] = useState([]);
  const [catalogueItems, setCatalogueItems] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(Boolean(user));
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);

  useEffect(() => {
    wakeBackend({ force: true, reason: "mobile-home" });
  }, []);

  useEffect(() => {
    let alive = true;
    if (!user) {
      setLibraryItems([]);
      setLoadingLibrary(false);
      return () => {
        alive = false;
      };
    }

    setLoadingLibrary(true);
    libraryAPI.list()
      .then((res) => {
        if (!alive) return;
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        setLibraryItems(items);
      })
      .catch(() => {
        if (alive) setLibraryItems([]);
      })
      .finally(() => {
        if (alive) setLoadingLibrary(false);
      });

    return () => {
      alive = false;
    };
  }, [user]);

  useEffect(() => {
    let alive = true;
    publicAPI.getCatalogue()
      .then((res) => {
        if (!alive) return;
        const products = Array.isArray(res.data) ? res.data : [];
        setCatalogueItems(products.filter((product) => product.thumbnail_path || product.cover_image_path).slice(0, 6));
      })
      .catch(() => {
        if (alive) setCatalogueItems([]);
      })
      .finally(() => {
        if (alive) setLoadingCatalogue(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const topButtons = useMemo(() => {
    if (!user) {
      return (
        <>
          <Button variant="outline" className="h-10 rounded-full border-[#2A4B46] text-[#2A4B46]" onClick={() => navigate("/login")}>
            <LogIn size={16} className="mr-2" /> Login
          </Button>
          <Button className="h-10 rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]" onClick={() => navigate("/register")}>
            S'inscrire
          </Button>
        </>
      );
    }

    return (
      <>
        <Button variant="outline" className="h-10 rounded-full border-[#2A4B46] text-[#2A4B46]" onClick={() => navigate("/dashboard")}>
          <UserCircle2 size={16} className="mr-2" /> Mon espace
        </Button>
        <Button className="h-10 rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]" onClick={() => navigate("/documents")}>
          Mes documents
        </Button>
      </>
    );
  }, [navigate, user]);

  const roleShortcuts = useMemo(() => {
    if (!user) {
      return (
        <>
          <RoleShortcut
            href="/public/catalogue"
            icon={Library}
            title="Explorer le catalogue"
            description="Voir les couvertures et ouvrir la boutique publique."
          />
          <RoleShortcut
            href="/login"
            icon={LogIn}
            title="Se connecter"
            description="Retrouver son espace personnel et ses téléchargements."
          />
        </>
      );
    }

    if (user.role === "admin") {
      return (
        <>
          <RoleShortcut href="/dashboard" icon={LayoutGrid} title="Dashboard admin" description="Accès aux outils de gestion selon le rang." />
          <RoleShortcut href="/admin/users" icon={Shield} title="Gestion des partenaires" description="Consulter, piloter et contrôler les comptes." />
          <RoleShortcut href="/admin/kyc" icon={FileText} title="KYC" description="Traiter les dossiers et validations des partenaires." />
        </>
      );
    }

    if (user.role === "partner") {
      return (
        <>
          <RoleShortcut href="/library" icon={Library} title="Ma bibliothèque" description="Mes ouvrages achetés et mes contenus autorisés." />
          <RoleShortcut href="/documents" icon={FileText} title="Mes documents" description="Contrat, confidentialité et dossiers déposés." />
          <RoleShortcut href="/products/submit" icon={ShoppingBag} title="Soumettre un produit" description="Déposer un livre, audio ou vidéo." />
        </>
      );
    }

    return (
      <>
        <RoleShortcut href="/library" icon={Library} title="Ma bibliothèque" description="Retrouver les contenus achetés et libres de droit." />
        <RoleShortcut href="/public/catalogue" icon={ShoppingBag} title="Catalogue" description="Découvrir puis acheter de nouveaux contenus." />
        <RoleShortcut href="/messages" icon={MessageCircle} title="Messagerie" description="Échanger avec l’équipe et le support." />
      </>
    );
  }, [user]);

  const libraryPreview = user ? libraryItems.slice(0, 4) : [];
  const noLibraryContent = user ? !loadingLibrary && libraryItems.length === 0 : true;

  return (
    <main className="min-h-dvh bg-[#FCFBF8] text-[#1A1D1C]">
      <section className="border-b border-[#E6E4E0]/80 bg-[#FCFBF8]/95 px-4 pb-6 pt-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={articoLogo} alt="MMJ" className="h-12 w-12 rounded-2xl object-contain" />
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#D4A373]">MMJ</p>
              <p className="text-xs text-[#5C625F]">Bibliothèque, catalogue et compte</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">{topButtons}</div>
        </div>

        <div className="mx-auto mt-5 max-w-6xl rounded-[28px] bg-gradient-to-br from-[#2A4B46] via-[#315f58] to-[#D4A373] px-5 py-6 text-white shadow-xl">
          <Badge className="rounded-full bg-white/15 text-white">Ouverture de l'application</Badge>
          <h1 className="mt-4 max-w-2xl text-2xl font-bold leading-tight font-['Outfit'] sm:text-3xl">
            Votre salon privé de lecture, d'écoute et de visionnage.
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-white/85">
            {MOBILE_MMJ_INTRO}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button className="rounded-full bg-white text-[#2A4B46] hover:bg-white/90" onClick={() => navigate("/library")}>
              Bibliothèque <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-full border-white/35 bg-white/10 text-white hover:bg-white/20" onClick={() => navigate("/public/catalogue")}>
              Catalogue public
            </Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl space-y-6 px-4 py-6 pb-24">
        <section className="space-y-4">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D4A373]">Ma bibliothèque</p>
              <h2 className="mt-1 text-xl font-bold text-[#2A4B46] font-['Outfit']">Vos contenus personnels</h2>
            </div>
            <Button variant="ghost" className="text-[#2A4B46]" onClick={() => navigate("/library")}>
              Ouvrir
            </Button>
          </div>
          {user ? (
            loadingLibrary ? (
              <Card className="border-[#E6E4E0]">
                <CardContent className="py-10 text-center text-sm text-[#5C625F]">Chargement de votre bibliothèque...</CardContent>
              </Card>
            ) : noLibraryContent ? (
              <Card className="border-[#E6E4E0]">
                <CardContent className="space-y-4 py-10 text-center">
                  <Library size={40} className="mx-auto text-[#2A4B46]" />
                  <div>
                    <p className="text-base font-semibold text-[#2A4B46]">Votre bibliothèque est vide.</p>
                    <p className="mt-2 text-sm leading-6 text-[#5C625F]">
                      Parcourez le catalogue pour découvrir des ouvrages libres de droit ou acquérir vos prochains contenus.
                    </p>
                  </div>
                  <Button className="rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]" onClick={() => navigate("/public/catalogue")}>
                    Voir le catalogue
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {libraryPreview.map((item) => (
                  <MobileCoverCard key={item.id} item={item} onOpen={() => navigate("/library")} dense />
                ))}
              </div>
            )
          ) : (
            <Card className="border-[#E6E4E0] bg-white">
              <CardContent className="space-y-4 py-8">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#E7EFEC] text-[#2A4B46]">
                    <Library size={20} />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-[#2A4B46]">Bibliothèque publique et personnelle</p>
                    <p className="mt-1 text-sm leading-6 text-[#5C625F]">
                      Ouvrez le catalogue pour explorer les œuvres, puis connectez-vous pour retrouver votre espace personnel.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button className="rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]" onClick={() => navigate("/public/catalogue")}>
                    Ouvrir le catalogue
                  </Button>
                  <Button variant="outline" className="rounded-full border-[#2A4B46] text-[#2A4B46]" onClick={() => navigate("/login")}>
                    Se connecter
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </section>

        <section className="space-y-4">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D4A373]">Catalogue public</p>
              <h2 className="mt-1 text-xl font-bold text-[#2A4B46] font-['Outfit']">Découvrir avant d'acheter</h2>
            </div>
            <Button variant="ghost" className="text-[#2A4B46]" onClick={() => navigate("/public/catalogue")}>
              Voir tout
            </Button>
          </div>
          {loadingCatalogue ? (
            <Card className="border-[#E6E4E0]">
              <CardContent className="py-10 text-center text-sm text-[#5C625F]">Chargement du catalogue...</CardContent>
            </Card>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {catalogueItems.map((item) => (
                <MobileCoverCard key={item.id} item={item} onOpen={() => navigate("/public/catalogue")} dense={false} />
              ))}
            </div>
          )}
        </section>

        <div className="grid gap-3 md:grid-cols-3">
          {roleShortcuts}
        </div>
      </section>
      <MobileBottomNav user={user} />

      <footer className="border-t border-[#E6E4E0] bg-white px-4 py-6">
        <div className="mx-auto max-w-6xl text-center">
          <p className="text-xs leading-5 text-[#5C625F]">
            (c)2025 Artico-MMJ. Tous droits réservés
          </p>
        </div>
      </footer>
    </main>
  );
}
